import React from "react";
import ReportShell from "@/components/ReportShell";
import { Field, TextInput, TextArea, Select, Checkbox } from "@/components/ReportPrimitives";
import { buildTripReport, tripReviewChecklist, TRIP_QUICK_PHRASES, tripTankDiff, fillVsExpectedDiff } from "@/lib/reports/trip";
import { tripStore } from "@/lib/reports/storage";
import { useReportRceWiring } from "@/lib/rce/useReportRceWiring";

const emptyState = () => ({
  id: null,
  header: {},
  tripSummary: {},
  tank: {},
  fill: {},
  flow: { performed: undefined },
  overpullDrag: {},
  observations: {},
  notes: {},
  review: {},
});

function QuickPhrases({ onPick, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {TRIP_QUICK_PHRASES.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPick(p)}
          className="text-[10.5px] px-2 py-1 rounded-md"
          style={{ background: "rgba(22,24,29,0.6)", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)" }}
        >
          + {p}
        </button>
      ))}
    </div>
  );
}

export default function TripSheet() {
  const rce = useReportRceWiring("tripSheet");
  const sections = [
    {
      id: "header", badge: "01", title: "Report Header", defaultOpen: true,
      render: (state, set) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Report Date"><TextInput type="date" value={state.header.reportDate} onChange={(v) => set("header", "reportDate", v)} testId="trip-h-reportDate" /></Field>
          <Field label="Rig Name"><TextInput value={state.header.rigName} onChange={(v) => set("header", "rigName", v)} testId="trip-h-rigName" /></Field>
          <Field label="Well Name"><TextInput value={state.header.wellName} onChange={(v) => set("header", "wellName", v)} testId="trip-h-wellName" /></Field>
          <Field label="Prepared By"><TextInput value={state.header.preparedBy} onChange={(v) => set("header", "preparedBy", v)} testId="trip-h-preparedBy" /></Field>
        </div>
      ),
    },
    {
      id: "tripSummary", badge: "02", title: "Trip Summary",
      render: (state, set) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Trip Direction"><Select value={state.tripSummary.direction} onChange={(v) => set("tripSummary", "direction", v)} options={["In hole", "Out of hole"]} testId="trip-t-direction" /></Field>
          <Field label="Pipe Size / Type"><TextInput value={state.tripSummary.pipe} onChange={(v) => set("tripSummary", "pipe", v)} testId="trip-t-pipe" /></Field>
          <Field label="Start Depth"><TextInput type="number" value={state.tripSummary.startDepth} onChange={(v) => set("tripSummary", "startDepth", v)} testId="trip-t-startDepth" /></Field>
          <Field label="End Depth"><TextInput type="number" value={state.tripSummary.endDepth} onChange={(v) => set("tripSummary", "endDepth", v)} testId="trip-t-endDepth" /></Field>
          <Field label="Stand Count"><TextInput type="number" value={state.tripSummary.standCount} onChange={(v) => set("tripSummary", "standCount", v)} testId="trip-t-standCount" /></Field>
        </div>
      ),
    },
    {
      id: "tank", badge: "03", title: "Trip Tank / Volume Tracking",
      render: (state, set) => {
        const diff = tripTankDiff(state);
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Trip Tank Start Volume"><TextInput type="number" value={state.tank.startVol} onChange={(v) => set("tank", "startVol", v)} testId="trip-tank-startVol" /></Field>
              <Field label="Trip Tank End Volume"><TextInput type="number" value={state.tank.endVol} onChange={(v) => set("tank", "endVol", v)} testId="trip-tank-endVol" /></Field>
            </div>
            <div className="text-[12px] font-semibold" data-testid="trip-tank-diff">
              Calculated Volume Change: <span className="text-[var(--rs-amber)]">{diff !== null ? diff : "—"}</span>
            </div>
          </>
        );
      },
    },
    {
      id: "fill", badge: "04", title: "Fill-Up / Displacement Record",
      render: (state, set) => {
        const d = fillVsExpectedDiff(state);
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Expected Fill / Displacement"><TextInput type="number" value={state.fill.expected} onChange={(v) => set("fill", "expected", v)} testId="trip-fill-expected" /></Field>
              <Field label="Actual Fill / Displacement"><TextInput type="number" value={state.fill.actual} onChange={(v) => set("fill", "actual", v)} testId="trip-fill-actual" /></Field>
            </div>
            <div className="text-[12px] font-semibold" data-testid="trip-fill-diff">
              Difference (Actual − Expected): <span className="text-[var(--rs-amber)]">{d !== null ? d : "—"}</span>
            </div>
          </>
        );
      },
    },
    {
      id: "flow", badge: "05", title: "Flow Checks",
      render: (state, set) => (
        <>
          <Checkbox checked={state.flow.performed} onChange={(v) => set("flow", "performed", v)} label="Flow Check Performed" testId="trip-flow-performed" />
          <Field label="Flow Check Result (as recorded)" helper="Enter observations as recorded. The tool does not certify flow-check results.">
            <TextArea value={state.flow.result} onChange={(v) => set("flow", "result", v)} rows={2} testId="trip-flow-result" />
          </Field>
        </>
      ),
    },
    {
      id: "overpullDrag", badge: "06", title: "Hole Fill / Overpull / Drag Notes",
      render: (state, set) => (
        <Field label="Notes"><TextArea value={state.overpullDrag.notes} onChange={(v) => set("overpullDrag", "notes", v)} rows={3} testId="trip-od-notes" /></Field>
      ),
    },
    {
      id: "observations", badge: "07", title: "Abnormal Observations",
      render: (state, set) => (
        <>
          <Field label="Swab / Surge Concerns"><TextArea value={state.observations.swabSurge} onChange={(v) => set("observations", "swabSurge", v)} rows={2} testId="trip-obs-swabSurge" /></Field>
          <Field label="Loss / Gain Observations"><TextArea value={state.observations.lossGain} onChange={(v) => set("observations", "lossGain", v)} rows={2} testId="trip-obs-lossGain" /></Field>
          <Field label="Other Observations"><TextArea value={state.observations.other} onChange={(v) => set("observations", "other", v)} rows={2} testId="trip-obs-other" /></Field>
        </>
      ),
    },
    {
      id: "notes", badge: "08", title: "Mud Engineer / Rig Notes",
      render: (state, set, helpers) => (
        <>
          <Field label="Rough Notes"><TextArea value={state.notes.roughNotes} onChange={(v) => set("notes", "roughNotes", v)} rows={3} testId="trip-n-roughNotes" /></Field>
          <QuickPhrases onPick={(p) => {
            helpers.setState((s) => ({ ...s, notes: { ...s.notes, roughNotes: ((s.notes.roughNotes || "") + (s.notes.roughNotes && !/\s$/.test(s.notes.roughNotes || "") ? " " : "") + p) } }));
          }} testId="trip-n-quick-phrases" />
        </>
      ),
    },
    {
      id: "review", badge: "09", title: "Supervisor Review",
      render: (state, set) => (
        <Field label="Supervisor Review Notes" helper="Supervisor signature required outside this tool.">
          <TextArea value={state.review.notes} onChange={(v) => set("review", "notes", v)} rows={2} testId="trip-r-notes" />
        </Field>
      ),
    },
  ];

  return (
    <ReportShell
      testIdPrefix="trip"
      reportType="tripSheet"
      safetyCritical={false}
      title="Trip Sheet"
      subtitle="Trip in/out, trip-tank, fill-up, flow checks"
      brandLine="Trip Sheet  ·  Draft Documentation Support"
      emptyState={emptyState}
      buildFn={buildTripReport}
      checklistFn={tripReviewChecklist}
      store={tripStore}
      sections={sections}
      initialState={rce.initialState}
      rceContextStrip={rce.rceContextStrip}
      filenameFromState={(s) => {
        const safe = (v) => (v || "").toString().replace(/[^a-z0-9-_]+/gi, "_").slice(0, 30);
        return `RigSentinel_Trip_${safe(s.header.wellName) || "well"}_${(s.header.reportDate || "").replace(/[^0-9]/g, "") || Date.now()}.pdf`;
      }}
    />
  );
}
